import React from 'react';

import classNames from 'classnames';
import { Field } from 'react-final-form';
import { useIntl } from '../../../util/reactIntl';

import { RangeSlider } from '../../../components';

import css from './IntegerRangeFilter.module.css';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

//  Ensures that the given values object has both minimum and maximum values.
//  If `values` does not include `minValue` or `maxValue`, defaults are applied.
const resolveMinMaxValues = (values, defaultMax, defaultMin) => {
  const { maxValue, minValue } = values || {};
  return {
    maxValue: maxValue != null ? maxValue : defaultMax,
    minValue: minValue != null ? minValue : defaultMin,
  };
};

const RangeInput = props => {
  const intl = useIntl();
  const {
    input,
    min: defaultMinValue,
    max: defaultMaxValue,
    step,
    isInSideBar,
    getLabelForRangeInput,
    onValueCommit,
    'aria-describedby': descriptionId,
  } = props;
  const { value: values = {}, onChange, onBlur, onFocus } = input;

  // Final Form owns the values, including temporary empty input while editing.
  // This also keeps the fields in sync when the filter is cleared or restored.
  const fieldValues = resolveMinMaxValues(values, defaultMaxValue, defaultMinValue);
  const { minValue, maxValue } = fieldValues;
  const isMinInvalid =
    minValue !== '' &&
    (!Number.isInteger(minValue) ||
      minValue < defaultMinValue ||
      minValue > defaultMaxValue ||
      (maxValue !== '' && minValue > maxValue));
  const isMaxInvalid =
    maxValue !== '' &&
    (!Number.isInteger(maxValue) ||
      maxValue < defaultMinValue ||
      maxValue > defaultMaxValue ||
      (minValue !== '' && maxValue < minValue));

  const sliderMin = clamp(
    Number.isFinite(minValue) ? minValue : defaultMinValue,
    defaultMinValue,
    defaultMaxValue
  );
  const sliderMax = clamp(
    Number.isFinite(maxValue) ? maxValue : defaultMaxValue,
    sliderMin,
    defaultMaxValue
  );
  const labelForRangeInput =
    getLabelForRangeInput ||
    ((value, handle) =>
      intl.formatMessage({ id: 'IntegerRangeFilter.screenreader.rangeHandle' }, { value, handle }));

  const handleMinValueChange = event => {
    const newValue = event.target.value === '' ? '' : Number(event.target.value);
    onChange({ ...fieldValues, minValue: newValue });
  };

  const handleMaxValueChange = event => {
    const newValue = event.target.value === '' ? '' : Number(event.target.value);
    onChange({ ...fieldValues, maxValue: newValue });
  };

  const finishEditing = nextValues => {
    if (nextValues.minValue !== minValue || nextValues.maxValue !== maxValue) {
      onChange(nextValues);
    }
    onBlur();
    onValueCommit?.();
  };

  const handleMinValueBlur = () => {
    const upperBound = clamp(
      Number.isFinite(maxValue) ? Math.round(maxValue) : defaultMaxValue,
      defaultMinValue,
      defaultMaxValue
    );
    finishEditing({ minValue: Math.min(Math.round(sliderMin), upperBound), maxValue: upperBound });
  };

  const handleMaxValueBlur = () => {
    finishEditing({ minValue: Math.round(sliderMin), maxValue: Math.round(sliderMax) });
  };

  const classes = isInSideBar ? css.formWrapper : null;

  return (
    <div className={classes}>
      <div className={classNames(css.contentWrapper, { [css.contentWrapperSidebar]: isInSideBar })}>
        <div className={css.inputsWrapper}>
          {!isInSideBar ? (
            <span className={css.labelPopup}>
              {intl.formatMessage({ id: 'FieldSelectIntegerRange.rangeLabel' })}
            </span>
          ) : null}
          <input
            className={classNames(css.minValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMinInvalid,
            })}
            type="number"
            min={defaultMinValue}
            max={defaultMaxValue}
            step={step}
            placeholder={defaultMinValue}
            value={fieldValues.minValue}
            onChange={handleMinValueChange}
            onFocus={onFocus}
            onBlur={handleMinValueBlur}
            aria-invalid={isMinInvalid}
            aria-describedby={descriptionId}
            aria-label={labelForRangeInput(sliderMin, 'min')}
          ></input>
          <span className={css.valueSeparator}>-</span>
          <input
            className={classNames(css.maxValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMaxInvalid,
            })}
            type="number"
            min={defaultMinValue}
            max={defaultMaxValue}
            placeholder={defaultMaxValue}
            step={step}
            value={fieldValues.maxValue}
            onChange={handleMaxValueChange}
            onFocus={onFocus}
            onBlur={handleMaxValueBlur}
            aria-invalid={isMaxInvalid}
            aria-describedby={descriptionId}
            aria-label={labelForRangeInput(sliderMax, 'max')}
          ></input>
        </div>
      </div>
      <div className={css.sliderWrapper}>
        <RangeSlider
          min={defaultMinValue}
          max={defaultMaxValue}
          step={step}
          handles={[sliderMin, sliderMax]}
          ariaLabels={[labelForRangeInput(sliderMin, 'min'), labelForRangeInput(sliderMax, 'max')]}
          onChangeEnd={onValueCommit}
          onChange={handles => {
            onChange({ minValue: handles[0], maxValue: handles[1] });
          }}
        />
      </div>
    </div>
  );
};

/**
 * FieldSelectIntegerRange component
 *
 * @component
 * @param {Object} props
 * @param {number} [props.min] - The minimum value
 * @param {number} [props.max] - The maximum value
 * @param {string} [props.name] - The name
 * @param {number} [props.step] - The step
 * @param {boolean} [props.isInSideBar] - Whether the filter is in the sidebar
 * @returns {JSX.Element}
 */
const FieldSelectIntegerRange = props => {
  const { max, min, name, step = 5, isInSideBar = false, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      step={step}
      isInSideBar={isInSideBar}
      component={RangeInput}
      {...rest}
    />
  );
};

export default FieldSelectIntegerRange;
